import { generatePath } from "react-router";

import type { Branch, BranchId } from "~/branching/model/branch";
import { resolveBranchIdForRoute } from "~/branching/model/resolve-default-branch";
import { routes } from "~/routes";

export type SystemHomeTab =
  | "index"
  | "directory"
  | "workflows"
  | "branches"
  | "settings"
  | "commits"
  | "changes-available"
  | "commit-detail"
  | "change-requests"
  | "change-request-detail"
  | "create-change-request";

/** The tabs whose route carries a selected directory node. */
export function tabHasDirectoryNode(tab: SystemHomeTab): boolean {
  return tab === "directory" || tab === "commit-detail" || tab === "changes-available";
}

export type ParsedSystemHomeRoute = {
  desiredBranchId: BranchId | null;
  tab: SystemHomeTab;
  nodeId: string | null;
  changeRequestId: string | null;
  commitId: string | null;
};

export type BuildSystemHomeRouteScope = {
  systemId: string;
  branchId?: BranchId | null;
  tab?: SystemHomeTab;
  nodeId?: string | null;
  changeRequestId?: string | null;
  commitId?: string | null;
};

export function buildStableSystemRoute(systemId: string): string {
  return `${routes.Systems}/${encodeURIComponent(systemId)}`;
}

export function buildResolvedSystemDetailPath(scope: { systemId: string; systemRedesign: boolean }): string {
  if (scope.systemRedesign) return buildSystemHomeRoute({ systemId: scope.systemId });
  return generatePath(routes.SystemDetails, { systemId: scope.systemId, "*": "" });
}

export function buildSystemHomeRoute(scope: BuildSystemHomeRouteScope): string {
  const tab = scope.tab ?? "index";
  const base = `${routes.SystemsDashboard}/${scope.systemId}`;
  const branchSegment = scope.branchId != null ? `/bid/${encodeURIComponent(scope.branchId)}` : "";

  switch (tab) {
    case "index":
      return `${base}${branchSegment}`;
    case "directory": {
      const directoryPath = `${base}${branchSegment}/directory`;
      return scope.nodeId ? `${directoryPath}/${encodeURIComponent(scope.nodeId)}` : directoryPath;
    }
    case "workflows":
      return `${base}${branchSegment}/workflows`;
    case "branches":
      return `${base}${branchSegment}/branches`;
    case "settings":
      return `${base}${branchSegment}/settings`;
    case "commits":
      return `${base}${branchSegment}/commits`;
    case "changes-available": {
      const changesPath = `${base}${branchSegment}/commits/changes-available`;
      return scope.nodeId ? `${changesPath}/${encodeURIComponent(scope.nodeId)}` : changesPath;
    }
    case "commit-detail": {
      if (!scope.commitId) {
        return `${base}${branchSegment}/commits`;
      }
      const detailPath = `${base}${branchSegment}/commits/${encodeURIComponent(scope.commitId)}`;
      return scope.nodeId ? `${detailPath}/${encodeURIComponent(scope.nodeId)}` : detailPath;
    }
    case "change-requests":
      return `${base}${branchSegment}/change-requests`;
    case "change-request-detail":
      if (!scope.changeRequestId) {
        return `${base}${branchSegment}/change-requests`;
      }
      return `${base}${branchSegment}/change-requests/${encodeURIComponent(scope.changeRequestId)}`;
    case "create-change-request":
      return `${base}${branchSegment}/change-requests/new`;
  }
}

function buildLegacySubsystemSnapshotHref(
  subsystem: { id: string; taggedConfiguration: { id: string } },
  snapshotId: string,
): string {
  return `/systems/${subsystem.id}/${subsystem.taggedConfiguration.id}/${snapshotId}`;
}

export function buildLegacySubsystemDetailsHref(subsystem: {
  id: string;
  taggedConfiguration: { id: string };
  taggedSnapshotId: string;
}): string {
  return buildLegacySubsystemSnapshotHref(subsystem, subsystem.taggedSnapshotId);
}

/** A System home tab scoped to the selected branch, addressed the way the route names it. */
export function buildBranchTabHref(
  systemId: string,
  tab: SystemHomeTab,
  selectedBranch: Branch | null,
  branches: Branch[],
): string {
  const branchId = selectedBranch ? resolveBranchIdForRoute(selectedBranch, branches) : null;
  return buildSystemHomeRoute({ systemId, branchId, tab });
}

export type BuildCommitDetailRouteScope = {
  systemId: string;
  branchId?: BranchId | null;
  commitId: string;
  nodeId?: string | null;
};

export function buildCommitDetailRoute(scope: BuildCommitDetailRouteScope): string {
  return buildSystemHomeRoute({
    systemId: scope.systemId,
    branchId: scope.branchId,
    tab: "commit-detail",
    commitId: scope.commitId,
    nodeId: scope.nodeId,
  });
}
